'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

// Types
interface HeroSlideLayout {
  /** Content alignment: 'center' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' */
  alignment: 'center' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  /** Maximum width for text content */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  /** Text alignment */
  textAlign?: 'left' | 'center' | 'right';
  /** Custom padding override */
  padding?: string;
}

interface HeroSlide {
  id: string;
  backgroundImage: string;
  title: string;
  subtitle: string;
  ctaText: string;
  ctaLink: string;
  alt: string;
  /** Layout configuration for this slide */
  layout: HeroSlideLayout;
  /** Whether to preload this image */
  priority?: boolean;
}

interface HeroCarouselProps {
  /** Auto-transition interval in milliseconds */
  interval?: number;
  /** Custom slides array */
  slides?: HeroSlide[];
  /** Whether to show navigation arrows */
  showNavigation?: boolean;
  /** Whether to show slide indicators */
  showIndicators?: boolean;
  /** Whether to pause on hover */
  pauseOnHover?: boolean;
}

// Configuration
const DEFAULT_INTERVAL = 5000;
const ANIMATION_CONFIG = {
  spring: { stiffness: 300, damping: 30 },
  opacity: { duration: 0.2 },
  stagger: {
    title: 0.4,
    subtitle: 0.5,
    cta: 0.6
  }
} as const;

// Default slides configuration
const defaultHeroSlides: HeroSlide[] = [
  // {
  //   id: 'canada-day',
  //   backgroundImage: '/images/canada-day/canada-day-banner-bg.webp',
  //   title: '',
  //   subtitle: '',
  //   ctaText: 'Shop Now',
  //   ctaLink: '/canada-day',
  //   alt: 'Canada Day Special Offers',
  //   layout: {
  //     alignment: 'center',
  //     maxWidth: 'lg',
  //     textAlign: 'center'
    
  //   },
  //   priority: true
  // },
  {
    id: 'flex-ac-elite',
backgroundImage: '/images/home/home-flex-ac-elite-bg.webp',
    title: '',
    subtitle: '',
    ctaText: 'Learn More',
    ctaLink: '/products/flex-ac-elite',
    alt: 'Flex AC Elite - Premium EV Charging Solution',
    layout: {
      alignment: 'bottom-left',
      maxWidth: 'md',
      textAlign: 'left',
      padding: 'pl-16 sm:pl-20 md:pl-24 lg:pl-28 pb-12 sm:pb-16 md:pb-20 lg:pb-24'

    },
    priority: true
  },  
  {
    id: 'flex-ac',
backgroundImage: '/images/home/home-flex-ac-bg.webp',
    title: 'Flex-AC',
    subtitle: 'Meet the Only EV Charger You\'ll Ever Need',
    ctaText: 'Learn More',
    ctaLink: '/products/flex-ac',
    alt: 'Flex-AC EV Charger',
    layout: {
      alignment: 'center',
      maxWidth: '2xl',
      textAlign: 'center'
    },
    priority: true
  },
  {
    id: 'ecosystem',
    backgroundImage: '/images/home/Pion Power ecosystem.webp',
    title: 'Sustainable Living',
    subtitle: 'Building a Future with Clean Energy',
    ctaText: '',
    ctaLink: '',
    alt: 'Pion Power Ecosystem - Sustainable Energy Solutions',
    layout: {
      alignment: 'center',
      maxWidth: '3xl',
      textAlign: 'center'
    }
  },
  {
    id: 'home-battery',
    backgroundImage: '/images/home/battery-top-page.webp',
    title: 'Home Battery',
    subtitle: 'Take Action for What You Believe In',
    ctaText: '',
    ctaLink: '',
    alt: 'Home Battery Storage Solutions',
    layout: {
      alignment: 'center',
      maxWidth: '2xl',
      textAlign: 'center'
    }
  }
];

// Utility functions
const getAlignmentClasses = (alignment: HeroSlideLayout['alignment']): string => {
  const alignmentMap = {
    'center': 'items-center justify-center',
    'bottom-left': 'items-start justify-end',
    'bottom-right': 'items-end justify-end',
    'top-left': 'items-start justify-start',
    'top-right': 'items-end justify-start'
  } as const;
  
  return alignmentMap[alignment];
};

const getMaxWidthClasses = (maxWidth: HeroSlideLayout['maxWidth'] = 'lg'): string => {
  const maxWidthMap = {
    'xs': 'max-w-xs',
    'sm': 'max-w-sm',
    'md': 'max-w-md',
    'lg': 'max-w-lg',
    'xl': 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl'
  } as const;
  
  return maxWidthMap[maxWidth];
};

const getTextAlignClasses = (textAlign: HeroSlideLayout['textAlign'] = 'center'): string => {
  const textAlignMap = {
    'left': 'text-left',
    'center': 'text-center',
    'right': 'text-right'
  } as const;
  
  return textAlignMap[textAlign];
};

// Animation variants
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      x: { type: 'spring', ...ANIMATION_CONFIG.spring },
      opacity: ANIMATION_CONFIG.opacity
    }
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: 'spring', ...ANIMATION_CONFIG.spring },
      opacity: ANIMATION_CONFIG.opacity
    }
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      x: { type: 'spring', ...ANIMATION_CONFIG.spring },
      opacity: ANIMATION_CONFIG.opacity
    }
  })
};

const contentVariants = {
  title: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: ANIMATION_CONFIG.stagger.title, duration: 0.6 }
  },
  subtitle: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: ANIMATION_CONFIG.stagger.subtitle, duration: 0.6 }
  },
  cta: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: ANIMATION_CONFIG.stagger.cta, duration: 0.6 }
  }
};

export function HeroCarousel({
  interval = DEFAULT_INTERVAL,
  slides = defaultHeroSlides,
  showNavigation = true,
  showIndicators = true,
  pauseOnHover = true
}: HeroCarouselProps) {
  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const direction = useRef<number>(1);

  // Memoized values
  const currentSlide = slides[currentIndex];
  const hasMultipleSlides = slides.length > 1;

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    if (!hasMultipleSlides) return;
    direction.current = -1;
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? slides.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSlideProgress(0);
  }, [currentIndex, slides.length, hasMultipleSlides]);

  const goToNext = useCallback(() => {
    if (!hasMultipleSlides) return;
    direction.current = 1;
    const isLastSlide = currentIndex === slides.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSlideProgress(0);
  }, [currentIndex, slides.length, hasMultipleSlides]);

  const goToSlide = useCallback(
    (index: number) => {
      if (!hasMultipleSlides || index === currentIndex) return;
      direction.current = index > currentIndex ? 1 : -1;
      setCurrentIndex(index);
      setSlideProgress(0);
    },
    [currentIndex, hasMultipleSlides]
  );

  // Auto-transition with optimized animation frame
  const updateProgress = useCallback(
    (timestamp: number) => {
      if (!lastUpdateTimeRef.current) lastUpdateTimeRef.current = timestamp;

      if (!isPaused && isVisible && hasMultipleSlides) {
        const elapsed = timestamp - lastUpdateTimeRef.current;
        const progress = Math.min((elapsed / interval) * 100, 100);
        setSlideProgress(progress);

        if (progress >= 100) {
          goToNext();
          lastUpdateTimeRef.current = timestamp;
        }
      }

      animationRef.current = requestAnimationFrame(updateProgress);
    },
    [isPaused, isVisible, interval, goToNext, hasMultipleSlides]
  );

  // Pause/resume handlers
  const pauseAutoTransition = useCallback(() => {
    if (pauseOnHover) {
      setIsPaused(true);
    }
  }, [pauseOnHover]);

  const resumeAutoTransition = useCallback(() => {
    if (pauseOnHover) {
      setIsPaused(false);
      lastUpdateTimeRef.current = performance.now();
    }
  }, [pauseOnHover]);

  const handleUserInteraction = useCallback(() => {
    if (!hasMultipleSlides) return;
    pauseAutoTransition();
    setTimeout(resumeAutoTransition, 3000);
  }, [pauseAutoTransition, resumeAutoTransition, hasMultipleSlides]);

  // Effects
  useEffect(() => {
    if (!hasMultipleSlides) return;

    // Intersection Observer for visibility
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setIsVisible(entry.isIntersecting);
          if (entry.isIntersecting) {
            lastUpdateTimeRef.current = performance.now();
          }
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Start animation loop
    lastUpdateTimeRef.current = performance.now();
    animationRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [updateProgress, hasMultipleSlides]);

  // Early return if no slides
  if (!slides.length || !currentSlide) {
    return null;
  }

  return (
    <section
      ref={containerRef}
      className="relative w-full"
      onMouseEnter={pauseAutoTransition}
      onMouseLeave={resumeAutoTransition}
    >
      <div className="relative overflow-hidden h-[200px] sm:h-[300px] md:h-[380px] lg:h-[600px] xl:h-[650px] mb-8 md:mb-12">
        <AnimatePresence initial={false} custom={direction.current} mode="popLayout">
          <motion.div
            key={currentIndex}
            className="absolute inset-0"
            custom={direction.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <Image
              src={currentSlide.backgroundImage}
              alt={currentSlide.alt}
              fill
              className="object-cover"
              style={{ objectPosition: 'center center' }}
              priority={currentSlide.priority === true}
              loading={currentSlide.priority === true ? 'eager' : 'lazy'}
              fetchPriority={currentSlide.priority === true ? 'high' : 'auto'}
              sizes="100vw"
              quality={85}
            />
            
            {/* Content Overlay */}
            <div className={`absolute inset-0 flex flex-col gap-4 px-4 sm:gap-6 sm:px-6 md:gap-8 md:px-8 ${
              getAlignmentClasses(currentSlide.layout.alignment)
            } ${
              currentSlide.layout.padding || ''
            }`}>
              <motion.div
                className={`text-white ${getMaxWidthClasses(currentSlide.layout.maxWidth)} ${getTextAlignClasses(currentSlide.layout.textAlign)}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                {currentSlide.title && (
                  <motion.h2
                    className="mb-2 text-3xl leading-tight font-bold sm:mb-3 sm:text-3xl md:mb-4 md:text-4xl lg:text-5xl"
                    {...contentVariants.title}
                  >
                    {currentSlide.title}
                  </motion.h2>
                )}
                {currentSlide.subtitle && (
                  <motion.p
                    className="text-normal leading-relaxed sm:text-lg md:text-xl lg:text-2xl"
                    {...contentVariants.subtitle}
                  >
                    {currentSlide.subtitle}
                  </motion.p>
                )}
              </motion.div>
              
              {/* CTA Button */}
              {currentSlide.ctaText && (
                <motion.div {...contentVariants.cta}>
                  <Link
                    href={currentSlide.ctaLink || '/products'}
                    className="inline-block rounded-md bg-white px-6 py-2 text-sm font-medium text-black transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black sm:px-8 sm:py-2.5 sm:text-base md:px-10 md:py-3"
                  >
                    {currentSlide.ctaText}
                  </Link>
                </motion.div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        {showNavigation && hasMultipleSlides && (
          <>
            <div className="absolute inset-y-0 left-2 z-30 flex items-center sm:left-4">
              <motion.button
                onClick={() => {
                  goToPrevious();
                  handleUserInteraction();
                }}
                className="rounded-full bg-black/30 p-1.5 text-white hover:bg-black/50 focus:outline-none focus:ring-2 focus:ring-white sm:p-2"
                aria-label="Previous slide"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
              </motion.button>
            </div>
            <div className="absolute inset-y-0 right-2 z-30 flex items-center sm:right-4">
              <motion.button
                onClick={() => {
                  goToNext();
                  handleUserInteraction();
                }}
                className="rounded-full bg-black/30 p-1.5 text-white hover:bg-black/50 focus:outline-none focus:ring-2 focus:ring-white sm:p-2"
                aria-label="Next slide"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                  />
                </svg>
              </motion.button>
            </div>
          </>
        )}

        {/* Slide Indicators */}
        {showIndicators && hasMultipleSlides && (
          <div className="absolute bottom-2 z-30 flex w-full justify-center gap-1.5 sm:bottom-4 sm:gap-2">
            {slides.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  goToSlide(index);
                  handleUserInteraction();
                }}
                className={`relative h-1.5 overflow-hidden rounded-full focus:outline-none focus:ring-1 focus:ring-white sm:h-2 ${
                  index === currentIndex ? 'w-6 bg-white/20 sm:w-8' : 'w-1.5 bg-white/50 sm:w-2'
                }`}
                aria-label={`Go to slide ${index + 1}`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                {index === currentIndex && (
                  <motion.span
                    className="absolute inset-0 origin-left bg-white"
                    style={{
                      transform: `translateX(${slideProgress - 100}%)`
                    }}
                    transition={{ ease: 'easeInOut' }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
